// src/pages/MerchantPortal.jsx
import { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { LogOut, TrendingUp, Wallet, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const mockPayments = [
  { id: 'pay_9X8Y7Z', amount: 8500, method: 'JazzCash', status: 'success', time: '2 min ago' },
  { id: 'pay_6A5B4C', amount: 12500, method: 'Easypaisa', status: 'pending', time: '5 min ago' },
  { id: 'pay_3D2E1F', amount: 4900, method: 'Bank', status: 'success', time: '12 min ago' },
];

export default function MerchantPortal() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [payments, setPayments] = useState(mockPayments);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(() => {
      setPayments(prev => [mockPayments[Math.floor(Math.random() * 3)], ...prev.slice(0, 4)]);
    }, 8000);
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (email && password) {
      setIsLoggedIn(true);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setEmail('');
    setPassword('');
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-md w-full p-10 bg-white rounded-3xl shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center text-3xl text-white font-bold shadow-lg">
              Z
            </div>
            <h1 className="text-4xl font-bold mt-6 font-poppins">Merchant Portal</h1>
            <p className="text-gray-600 mt-2">Login to manage payments</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <input
              type="email"
              placeholder="merchant@store.pk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-5 py-4 border rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary transition"
              required
            />
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-5 py-4 border rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary transition"
              required
            />
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-primary to-accent text-white py-4 rounded-xl font-bold text-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Login to Portal
            </button>
          </form>

          <p className="text-center mt-6 text-sm text-gray-500">
            Need access? <button onClick={() => navigate('/contact')} className="text-primary font-semibold">Contact Sales</button>
          </p>
        </div>
      </div>
    );
  }

  const revenueData = [
    { day: 'Mon', amount: 42000 },
    { day: 'Tue', amount: 58000 },
    { day: 'Wed', amount: 49000 },
    { day: 'Thu', amount: 71000 },
    { day: 'Fri', amount: 89000 },
    { day: 'Sat', amount: 65000 },
    { day: 'Sun', amount: 52000 },
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="text-5xl font-bold font-poppins bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Merchant Portal
            </h1>
            <p className="text-xl text-gray-600">Welcome back, <span className="font-semibold">{email}</span></p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-semibold hover:shadow-2xl hover:scale-105 transition-all"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>

        {/* Stats - White + Soft Blue */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {[
            { label: 'Today\'s Revenue', value: 'PKR 89,000', icon: TrendingUp, color: 'text-green-600' },
            { label: 'Success Rate', value: '98.7%', icon: CheckCircle, color: 'text-primary' },
            { label: 'Pending Payout', value: 'PKR 142,500', icon: Wallet, color: 'text-yellow-600' },
            { label: 'Total Volume', value: 'PKR 1.2M', icon: AlertCircle, color: 'text-violet-600' },
          ].map((stat, i) => (
            <div key={i} className="bg-white p-8 rounded-3xl shadow-lg border border-blue-100">
              <div className="flex items-center justify-between mb-4">
                <stat.icon className={`w-10 h-10 ${stat.color}`} />
                <span className="text-3xl font-bold">{stat.value}</span>
              </div>
              <p className="text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Charts - Clean White */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white p-8 rounded-3xl shadow-lg border border-blue-100">
            <h3 className="text-2xl font-bold mb-6">Revenue This Week</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="amount" stroke="#8A5CFF" strokeWidth={3} dot={{ fill: '#8A5CFF' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-8 rounded-3xl shadow-lg border border-blue-100">
            <h3 className="text-2xl font-bold mb-6">Payment Methods</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { method: 'JazzCash', count: 428 },
                { method: 'Easypaisa', count: 392 },
                { method: 'Bank', count: 180 },
                { method: 'Card', count: 95 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="method" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#1A6AE4" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Live Feed - Clean White */}
        <div className="bg-white p-8 rounded-3xl shadow-lg border border-blue-100">
          <h3 className="text-2xl font-bold mb-6">Live Payment Feed</h3>
          <div className="space-y-4">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${p.status === 'success' ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`} />
                  <div>
                    <p className="font-semibold">PKR {p.amount.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">{p.method}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium capitalize">{p.status}</p>
                  <p className="text-xs text-gray-500">{p.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}